import React, {useRef, useState} from 'react';
import {message} from 'antd';
import type {CompositeAiAnalysis, CompositeSignal} from '../../../../types/compositeSignal';
import {analyzeCompositeSignal, getCompositeSignalAnalysis,} from '../../../../services/compositeSignalService';
import {EvidenceChainBlock} from './EvidenceChainBlock';
import {CompositeKlinePanel} from './CompositeKlinePanel';

interface CompositeSignalCardProps {
    signal: CompositeSignal;
    expanded: boolean;
    onToggle: () => void;
    apiKeyId?: number;
}

function fmtPrice(v: number | null): string {
    return null == v ? '--' : Number(v).toPrecision(6).replace(/\.?0+$/, '');
}

/**
 * 卡片五态推导：冲突中性 / 过期 / 共振多空 / 无目标位降级在共振态内处理
 */
function deriveState(s: CompositeSignal): 'conflict' | 'expired' | 'directional' {
    if ('EXPIRED' === s.status) return 'expired';
    const hasBothGroups = !!s.evidence?.bullGroup && !!s.evidence?.bearGroup;
    if (0 === s.direction || hasBothGroups) return 'conflict';
    return 'directional';
}

function levelLabel(s: CompositeSignal): string {
    switch (s.signalLevel) {
        case 'STRONG':
            return '明确';
        case 'WATCH':
            return '试探';
        default:
            return '观望';
    }
}

function directionText(dir: number): string {
    if (1 === dir) return '看多';
    if (-1 === dir) return '看空';
    return '中性';
}

function formatTriggerTime(ts: number): string {
    const d = new Date(ts);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const CompositeSignalCard: React.FC<CompositeSignalCardProps> = ({
                                                                            signal,
                                                                            expanded,
                                                                            onToggle,
                                                                            apiKeyId,
                                                                        }) => {
    const state = deriveState(signal);
    const [aiData, setAiData] = useState<CompositeAiAnalysis | null>(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState<string | null>(null);
    // 本卡片的K线图展开状态：各 Item 独立管理，多卡片可同时展开互不共享
    const [chartOpen, setChartOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    const handleToggleChart = () => {
        setChartOpen(prev => {
            const next = !prev;
            // 展开后滚动到本卡片的面板，确保图表进入视口
            if (next) {
                requestAnimationFrame(() => {
                    panelRef.current?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
                });
            }
            return next;
        });
    };

    const handleAiClick = async () => {
        if (aiLoading) return;
        setAiLoading(true);
        setAiError(null);
        try {
            // 有缓存直接展示，无缓存触发 LLM 解读（120s 超时由 service 层控制）
            let data = await getCompositeSignalAnalysis(signal.id).catch(() => null);
            if (!data) {
                data = await analyzeCompositeSignal(signal.id);
            }
            if (data) {
                setAiData(data);
            } else {
                setAiError('解读失败，请重试');
                message.error('AI解读失败，请重试');
            }
        } catch {
            setAiError('模型响应超时');
            message.error('模型响应超时，请重试');
        } finally {
            setAiLoading(false);
        }
    };

    const dirClass = 1 === signal.direction ? 'bull' : -1 === signal.direction ? 'bear' : 'neutral';
    const summaryReason = signal.evidence?.reasonText ?? '';
    const showOperations = 'conflict' !== state && null != signal.entryPrice;

    return (
        <div
            className={`pattern-card cs-card${-1 === signal.direction ? ' bear-card' : ''}${'expired' === state ? ' cs-expired' : ''}`}>
            {'expired' === state && <div className="cs-expired-badge">已过期</div>}
            <div className="pc-header" onClick={onToggle}>
                <span className="pc-name">
                    {directionText(signal.direction)}{1 === signal.direction ? '↑' : -1 === signal.direction ? '↓' : '↕'}
                    <span className={`pc-dir ${dirClass}`}>{levelLabel(signal)}</span>
                </span>
                <div className="pc-meta">
                    <span>{signal.symbol.replace('-SWAP', '').replace('-USDT', '/USDT')} · {signal.interval}</span>
                    <span>触发 {formatTriggerTime(signal.triggerKlineTime)}</span>
                    <span
                        className="pc-conf">置信度 {null != signal.compositeConfidence ? Number(signal.compositeConfidence) : '--'}</span>
                    <span className="cs-toggle">{expanded ? '收起 ▴' : '证据 ▾'}</span>
                </div>
            </div>

            {'conflict' === state && (
                <div className="cs-conflict-tip">多空僵持，观望 —— 同窗口内多空两组有效证据并存</div>
            )}
            {'expired' === state && signal.expireReason && (
                <div className="cs-expire-reason">过期原因：{signal.expireReason}</div>
            )}

            {summaryReason && <div className="cs-summary">{summaryReason}</div>}

            {showOperations ? (
                <div className="cs-operations">
                    <span><i className="cs-op-label">入场</i>{fmtPrice(signal.entryPrice)}</span>
                    <span><i className="cs-op-label">止损</i>{fmtPrice(signal.stopLossPrice)}</span>
                    <span>
                        <i className="cs-op-label">目标</i>
                        {null == signal.targetPrice
                            ? '--（无明确目标位，建议人工研判）'
                            : fmtPrice(signal.targetPrice)}
                    </span>
                    <span><i
                        className="cs-op-label">风报比</i>{null == signal.riskRewardRatio ? '--' : Number(signal.riskRewardRatio)}</span>
                </div>
            ) : null}

            <div className="cs-actions">
                <button
                    className="cs-action-btn"
                    disabled={aiLoading}
                    onClick={handleAiClick}
                >
                    {aiLoading ? '解读中…' : aiData ? '重看解读' : 'AI解读'}
                </button>
                <button
                    className={`cs-action-btn${chartOpen ? '' : ' ghost'}`}
                    onClick={handleToggleChart}
                >
                    {chartOpen ? '收起K线图 ▴' : '展开K线图 ▾'}
                </button>
            </div>

            {/* 本 Item 的K线图：内嵌卡片内，多个信号可各自展开互不共享 */}
            {chartOpen && (
                <div ref={panelRef}>
                    <CompositeKlinePanel
                        signal={signal}
                        apiKeyId={apiKeyId}
                        onClose={() => setChartOpen(false)}
                    />
                </div>
            )}

            {aiError && !aiData && <div className="cs-ai-error">{aiError}</div>}
            {(aiData || aiLoading) && (
                <div className="cs-ai-block">
                    {aiLoading ? <div className="cs-ai-loading">模型解读中…</div> : (
                        <>
                            <div className="cs-ai-model">{aiData!.modelName}</div>
                            <div className="cs-ai-text">{aiData!.reasoning}</div>
                        </>
                    )}
                </div>
            )}

            {expanded && <EvidenceChainBlock evidence={signal.evidence}/>}
        </div>
    );
};
